import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import { useCameraCaptures } from '../../contexts/cameraCapturesContext';
import { useCameraStream } from '../../hooks/useCameraStream';
import { useFaceMatching } from '../../hooks/useFaceMatching';
import { useSettings } from '../../contexts/settingsContext';
import { UI_TIMINGS } from '../../config/appConfig';
import CancelIcon from '@mui/icons-material/Cancel';
import '../css/StealthCamera.css';

/**
 * Stealth Video Recorder
 *
 * Auto-captures frames from video stream every N seconds
 * Exactly like StealthCamera but automatic instead of tap-to-capture
 */
export default function StealthVideoRecorder({ open, onClose }) {
  const canvasRef = useRef(null);
  const timerRef = useRef(null);

  const [captureCount, setCaptureCount] = useState(0);
  const [matchCount, setMatchCount] = useState(0);
  const [flash, setFlash] = useState(false);

  const { addCapture } = useCameraCaptures();
  const settings = useSettings();
  const { videoFrameInterval } = settings;

  // Camera error handler - stable reference to prevent re-renders
  const handleCameraError = useCallback((err) => {
    console.error('Camera error:', err);
  }, []);

  // Camera stream
  const { videoRef, isRunning, error, startCamera, stopCamera, captureFrame } = useCameraStream({
    autoStart: false,
    onError: handleCameraError
  });

  // Match callbacks
  const handleMatchSuccess = useCallback(() => {
    setMatchCount(prev => prev + 1);
  }, []);

  const handleNoMatch = useCallback(() => {
    // Silent - no match
  }, []);

  const handleError = useCallback((err) => {
    console.error('Match error:', err);
  }, []);

  // Face matching
  const { matchFace } = useFaceMatching({
    silent: true,
    onSuccess: handleMatchSuccess,
    onNoMatch: handleNoMatch,
    onError: handleError
  });

  // Start/stop camera on open/close
  useEffect(() => {
    if (open) {
      setCaptureCount(0);
      setMatchCount(0);
      startCamera({ video: { facingMode: 'environment' }, audio: false });
    } else {
      stopCamera();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only run when 'open' changes, not when startCamera/stopCamera change

  // Auto-capture timer
  useEffect(() => {
    if (!isRunning || !open) {
      return;
    }

    const doCapture = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Flash animation
      setFlash(true);
      setTimeout(() => setFlash(false), UI_TIMINGS.CAMERA_FLASH_DURATION);

      const dataUrl = captureFrame(canvas);
      if (!dataUrl) return;

      const captureId = addCapture(dataUrl, 'stealth_video');
      setCaptureCount(prev => prev + 1);

      // Fire-and-forget: Don't await, let processing happen in background
      // This ensures frames are captured every N seconds regardless of processing time
      matchFace(captureId, dataUrl).catch(err => {
        console.error('matchFace error:', err);
      });
    };

    // First capture immediately
    doCapture();

    // Then every N seconds
    timerRef.current = setInterval(doCapture, videoFrameInterval * 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRunning, open, videoFrameInterval, captureFrame, addCapture, matchFace]);

  const handleClose = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    stopCamera();
    onClose();
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: 'black',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
    >
      <div
        className="stealth-camera-viewport"
        style={{
          width: '100%',
          height: '100%',
          position: 'relative'
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />

        {/* Flash effect */}
        {flash && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'white',
              animation: 'flash 120ms ease-out',
              pointerEvents: 'none'
            }}
          />
        )}

        {/* Counter */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            color: 'white',
            fontSize: '14px',
            fontWeight: 500,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            padding: '6px 12px',
            borderRadius: '4px',
            opacity: 0.7,
            fontFamily: 'monospace',
            letterSpacing: '0.5px'
          }}
        >
          {matchCount} / {captureCount}
        </Box>

        {/* Error */}
        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              textAlign: 'center',
              padding: 2,
              bgcolor: 'rgba(0,0,0,0.7)',
              borderRadius: 1
            }}
          >
            Error: {error}
          </Box>
        )}
      </div>

      {/* Exit button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          bottom: 24,
          left: 24,
          width: 64,
          height: 64,
          bgcolor: 'red',
          color: 'white',
          '&:hover': {
            bgcolor: '#b71c1c'
          },
          boxShadow: 3
        }}
      >
        <CancelIcon sx={{ fontSize: 40 }} />
      </IconButton>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Box>
  );
}
